import * as Sentry from '@sentry/react';
import type { User, UserRole } from '@shared/types';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { apiUrl } from '@/config/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  hasRole: (roles: UserRole[]) => boolean;
  hasDepartmentAccess: (departmentCode: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'admission-system-auth';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed.user);
      } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${apiUrl('/api/auth/login')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const result = (await response.json()) as {
        success: boolean;
        data?: { user: User; accessToken: string };
        error?: string;
      };

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'ログインに失敗しました',
        };
      }

      if (result.success && result.data) {
        const { user, accessToken } = result.data;
        setUser(user);
        Sentry.setUser({ id: user.id, email: user.email });
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user, accessToken }));
        // Set token for subsequent requests (if using global header config)
        return { success: true };
      }

      return { success: false, error: '予期せぬエラーが発生しました' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'サーバーに接続できませんでした' };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    Sentry.setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }, []);

  const hasRole = useCallback(
    (roles: UserRole[]): boolean => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user],
  );

  const hasDepartmentAccess = useCallback(
    (departmentCode: string): boolean => {
      if (!user) return false;
      // Admin roles have access to all departments
      if (user.role === 'system_admin' || user.role === 'univ_admin') {
        return true;
      }
      // Department staff only have access to their department
      if (user.role === 'dept_staff' && user.departmentCode) {
        return user.departmentCode === departmentCode;
      }
      return false;
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        hasRole,
        hasDepartmentAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
